/**
 * Visual style fallback data for the frontend.
 *
 * Postgres (Cloud SQL) is the single source of truth for styles. This array
 * is used ONLY as placeholder data while the API query loads (see
 * `fallbackStyles()` in hooks.ts) and for `getStyleOption()` label lookups
 * when a style hasn't been fetched yet. Keep it in sync with the
 * `public_styles` table.
 */

export const VISUAL_STYLES = [
  {
    value: 'bold-manhwa',
    label: 'Bold Manhwa',
    tags: ['Korean', 'Illustrative', 'Anime'],
  },
  {
    value: 'chibi-animal-cartoon',
    label: 'Chibi Animal Cartoon',
    tags: ['Cute Chibi', 'Eastern'],
  },
  {
    value: 'detective-conan',
    label: 'Detective Conan',
    tags: ['Anime', 'Pop Culture', 'Eastern'],
  },
  {
    value: 'elegant-cinematic-3d',
    label: 'Elegant Cinematic 3D',
    tags: ['3D', 'Eastern'],
  },
  {
    value: 'flat-vector-pop',
    label: 'Flat Vector Pop',
    tags: ['Illustrative', 'Pop Culture'],
  },
  {
    value: 'high-fidelity-anime',
    label: 'High-Fidelity Anime',
    tags: ['Anime', 'Illustrative', 'Pop Culture'],
  },
  {
    value: 'neon-cel-shade',
    label: 'Neon Cel-Shade',
    tags: ['Illustrative', 'Pop Culture'],
  },
  {
    value: 'nostalgic-detective-anime',
    label: 'Nostalgic Detective Anime',
    tags: ['Anime', 'Pop Culture', 'Illustrative'],
  },
  {
    value: 'pixar-3d-animation',
    label: 'Pixar 3D Animation',
    tags: ['3D', 'Western', 'Pop Culture'],
  },
  {
    value: 'pixar-like-3d',
    label: 'Pixar-Like 3D',
    tags: ['3D', 'Western', 'Pop Culture'],
  },
  {
    value: 'retro-geometric-nocturne',
    label: 'Retro Geometric Nocturne',
    tags: ['Illustrative', 'Western'],
  },
  {
    value: 'romance-fantasy',
    label: 'Romance Fantasy',
    tags: ['Korean', 'Illustrative'],
  },
  {
    value: 'soft-watercolor',
    label: 'Soft Watercolor',
    tags: ['Illustrative'],
  },
  {
    value: 'stylized-pop-cgi',
    label: 'Stylized Pop CGI',
    tags: ['3D', 'Korean', 'Pop Culture'],
  },
  {
    value: 'vintage-storybook',
    label: 'Vintage Storybook',
    tags: ['Illustrative', 'Western'],
  },
]

export type VisualStyle = (typeof VISUAL_STYLES)[number]['value'] | string

// General creative agent: no house style by default. Empty string = "no style".
// A style is applied only when the user explicitly picks one.
export const DEFAULT_STYLE: VisualStyle = ''

/**
 * Tag values that can be assigned to styles. These are the actual enum values
 * stored on style objects in `style.json` and returned by the API.
 */
export const STYLE_TAGS = [
  'Pop Culture',
  'Anime',
  'Illustrative',
  'Korean',
  'Western',
  '3D',
  'Eastern',
  'Cute Chibi',
]

/**
 * Filter tabs shown in the Style Library modal.
 * "All" and "Recently Used" are virtual filters (not stored on styles).
 */
export const STYLE_FILTER_TABS = [
  'All',
  'Recently Used',
  ...STYLE_TAGS,
]

/**
 * Tabs that are disabled (not yet implemented).
 */
export const DISABLED_FILTER_TABS = new Set(['Recently Used'])

/**
 * Get style option by value
 */
export function getStyleOption(value: string) {
  return VISUAL_STYLES.find((s) => s.value === value)
}
